import type { ContractStatus } from '@forecastify/shared';
import type { Toast } from '@forecastify/ui';
import type { Translate } from '../../i18n';
import type { SessionState } from '../../session';
import { bulkTargets, postBulk } from '../use-bulk';

let toastCounter = 0;

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The selection's status — `POST /contracts/bulk`, 14 Oct 2026.             │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **Once, for both lists that draw `ContractList`** — `/contracts` and a project's own tab — so
 * the accounting and the reload cannot drift between them. Returns the toast rather than showing
 * it, because the two callers own different toasters.
 *
 * ⚠️ **The toast counts what LANDED**, not what was selected (`state-rules.md` §5), and a row
 * somebody else has moved since the page loaded comes back in `failed` rather than overwritten.
 */
export async function bulkContractStatus(
  session: SessionState,
  contracts: readonly { id: string; version: number }[],
  ids: readonly string[],
  status: ContractStatus,
  reload: () => Promise<void>,
  t: Translate,
): Promise<Toast> {
  const result = await postBulk(session, '/contracts/bulk', {
    status,
    targets: bulkTargets(contracts, ids),
  });
  await reload();
  toastCounter += 1;
  const id = `contracts-bulk-${String(toastCounter)}`;
  if (result === null) return { id, title: t('error.unavailable'), variant: 'error' };
  return {
    id,
    title: t('contracts.toast.bulkStatus', { count: result.done.length }),
    ...(result.failed.length === 0
      ? {}
      : { description: t('bulk.someRefused', { total: ids.length }) }),
    variant: result.failed.length === 0 ? 'success' : 'error',
  };
}
